import { localize } from "client_legacy/ndaw/culture";
import {
	UpdateIntegrationCredentialsPasswordViewModel,
	ignorePossiblePasswordChange,
	checkLinkPasswordState,
	PasswordStateResultModel,
	LinkIdentifierModel
} from "client/Api/IntegrationCredentials/integrationCredentials";
import { useUpdatePasswordDialog } from "./UpdatePasswordDialog";
import {
	ConfirmationDialog,
	FormattedText,
	renderReactDialog,
	Typography
} from "client/Components";
import { ReactElement, useCallback } from "react";

export type SyncPasswordDialogProps = {
	model: UpdateIntegrationCredentialsPasswordViewModel;
	onClose: (wasUpdated: boolean) => void;
};

export const SyncPasswordDialog = (props: SyncPasswordDialogProps): ReactElement => {
	const {
		model,
		onClose
	} = props;

	const [updatePasswordDialog, openUpdatePasswordDialog] = useUpdatePasswordDialog();

	const confirmationHandler = useCallback(
		async (): Promise<void> => {
			const updateResult: TryResult<void> = await openUpdatePasswordDialog({
				credentials: model
			});

			if (updateResult.wasSuccessful) {
				onClose(true);
			}
		},
		[
			model,
			onClose,
			openUpdatePasswordDialog
		]
	);

	const cancellationHandler = useCallback(
		async (): Promise<void> => {
			try {
				await ignorePossiblePasswordChange(model.id);
			} catch (e) {
				console.error(e);
			}

			onClose(false);
		},
		[model.id, onClose]
	);

	if (updatePasswordDialog) {
		return updatePasswordDialog;
	}

	return (
		<ConfirmationDialog
			title={localize("_PasswordChanged")}
			confirmText={localize("_UpdatePassword")}
			trackingId="integration-credentials-password-changed-action-dialog"
			cancelText={localize("_DontUpdate")}
			onConfirmation={confirmationHandler}
			onCancellation={cancellationHandler}
		>
			<FormattedText
				text={localize("_IntegrationCredentialsPasswordPossibleChangedMessage")}
				thingsToReplace={[
					{
						what: "{{Username}}",
						filler: (
							<Typography
								weight="bold"
							>
								{model.username}
							</Typography>
						)
					},
					{
						what: "{{AppOrService}}",
						filler: model.linkNames.join(", ")
					}
				]}
			/>
		</ConfirmationDialog>
	);
};
SyncPasswordDialog.displayName = "SyncPasswordDialog";

export type SyncPasswordDialogInitialData = Omit<
	SyncPasswordDialogProps,
	"onClose"
>;

export function openSyncPasswordDialog(
	props: SyncPasswordDialogInitialData
): Promise<boolean> {

	return new Promise<boolean>(
		(resolve): void => {
			renderReactDialog(
				(onClose): ReactElement => (
					<SyncPasswordDialog
						{...props}
						onClose={(wasUpdated) => {
							onClose();
							resolve(wasUpdated);
						}}
					/>
				)
			);
		}
	);
}

export async function offerPasswordUpdateWhenPossibleChanged(
	linkIdentifier: LinkIdentifierModel
): Promise<boolean> {
	try {
		const state: PasswordStateResultModel = await checkLinkPasswordState(linkIdentifier);

		if (state.isPossibleChanged) {
			return await openSyncPasswordDialog({
				model: state.changedViewModel
			});
		} else {
			return false;
		}
	} catch (e) {
		console.error(e);
		return false;
	}
}
